import { useRef, useState } from "react";
import { Reply, ReplyAll, Forward, Send, Loader2, X, Bold, Italic, Underline, List, ListOrdered } from "lucide-react";
import { toast } from "sonner";

const who = (a) => (a?.name ? `${a.name} <${a.email}>` : a?.email || "");
const quote = (msg) =>
  `\n\n--- Original message ---\nFrom: ${who(msg.from)}\nDate: ${new Date(msg.date).toLocaleString("en-IN")}\nSubject: ${msg.subject}\n\n${msg.text || ""}`;

// Outlook-style inline reply that sends without opening the full composer.
export const InlineReply = ({ api, message, activeEmail, onForward, onSent, onUndoableSend }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("reply");
  const [busy, setBusy] = useState(false);
  const editorRef = useRef(null);

  const recipients = () => {
    const self = (activeEmail || "").toLowerCase();
    if (mode === "reply") return { to: message.from?.email || "", cc: "" };
    const seen = new Set([self]);
    const to = [message.from, ...(message.to || [])].filter((a) => {
      const e = (a?.email || "").toLowerCase();
      if (!e || seen.has(e)) return false; seen.add(e); return true;
    });
    const cc = (message.cc || []).filter((a) => {
      const e = (a?.email || "").toLowerCase();
      if (!e || seen.has(e)) return false; seen.add(e); return true;
    });
    return { to: to.map((a) => a.email).join(", "), cc: cc.map((a) => a.email).join(", ") };
  };

  const start = (m) => { setMode(m); setOpen(true); setTimeout(() => editorRef.current?.focus(), 50); };

  const send = async () => {
    const text = editorRef.current?.innerText?.trim() || "";
    const html = editorRef.current?.innerHTML || "";
    if (!text) { toast.error("Write a message first"); return; }
    const { to, cc } = recipients();
    if (!to) { toast.error("No recipient to reply to"); return; }
    setBusy(true);
    try {
      const subject = message.subject?.startsWith("Re:") ? message.subject : `Re: ${message.subject}`;
      const bodyHtml = `${html}<br/><br/><div style="color:#888;font-size:12px;">${quote(message).replace(/\n/g, "<br/>")}</div>`;
      const fd = new FormData();
      fd.append("to", to);
      fd.append("cc", cc);
      fd.append("bcc", "");
      fd.append("from_account", activeEmail);
      fd.append("subject", subject);
      fd.append("body", `${text}${quote(message)}`);
      fd.append("body_html", bodyHtml);
      fd.append("include_signature", "true");
      // same Undo-send countdown as the composer: schedule ~11s out and let the user cancel
      fd.append("send_at", new Date(Date.now() + 11000).toISOString());
      fd.append("undo", "true");
      const { data } = await api.post("/mail/schedule", fd);
      if (editorRef.current) editorRef.current.innerHTML = "";
      setOpen(false);
      onUndoableSend?.({ sid: data.id, snapshot: { to, cc, bcc: "", subject, body: `${text}${quote(message)}`, body_html: bodyHtml } });
      onSent?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send the reply");
    } finally {
      setBusy(false);
    }
  };

  const exec = (cmd) => { editorRef.current?.focus(); document.execCommand(cmd, false, null); };
  const tBtn = "p-1.5 text-ol-sub hover:text-ol-blue hover:bg-white rounded-[4px] transition-colors";

  const link = "flex items-center gap-1.5 text-[13px] text-ol-blue hover:bg-ol-hover rounded-[4px] px-2.5 py-1.5 transition-colors";

  if (!open) {
    return (
      <div className="flex flex-wrap gap-1 pt-2" data-testid="inline-reply-bar">
        <button onClick={() => start("reply")} className={link} data-testid="inline-reply-btn"><Reply size={15} /> Reply</button>
        <button onClick={() => start("replyAll")} className={link} data-testid="inline-replyall-btn"><ReplyAll size={15} /> Reply all</button>
        <button onClick={() => onForward?.(message)} className={link} data-testid="inline-forward-btn"><Forward size={15} /> Forward</button>
      </div>
    );
  }

  const { to } = recipients();
  return (
    <div className="mt-2 border border-ol-line rounded-[6px] overflow-hidden" data-testid="inline-reply-box">
      <div className="flex items-center justify-between px-3 py-1.5 bg-ol-sel text-[12px] text-ol-text">
        <span className="truncate">{mode === "reply" ? "Reply" : "Reply all"} to <span className="font-semibold">{to}</span></span>
        <button onClick={() => setOpen(false)} className="text-ol-sub hover:text-ol-text p-1" aria-label="Close reply" data-testid="inline-reply-close"><X size={14} /></button>
      </div>
      <div ref={editorRef} contentEditable suppressContentEditableWarning className="min-h-[90px] px-3 py-2.5 text-[14px] text-ol-text focus:outline-none" style={{ fontFamily: '"Segoe UI", Arial, sans-serif' }} data-testid="inline-reply-editor" />
      <div className="flex items-center gap-0.5 px-2 py-1 border-t border-ol-line bg-ol-bg" data-testid="inline-reply-toolbar">
        <button type="button" onClick={() => exec("bold")} className={tBtn} title="Bold" data-testid="inline-fmt-bold"><Bold size={14} /></button>
        <button type="button" onClick={() => exec("italic")} className={tBtn} title="Italic" data-testid="inline-fmt-italic"><Italic size={14} /></button>
        <button type="button" onClick={() => exec("underline")} className={tBtn} title="Underline" data-testid="inline-fmt-underline"><Underline size={14} /></button>
        <span className="w-px h-4 bg-ol-line mx-1" />
        <button type="button" onClick={() => exec("insertUnorderedList")} className={tBtn} title="Bulleted list" data-testid="inline-fmt-bullets"><List size={14} /></button>
        <button type="button" onClick={() => exec("insertOrderedList")} className={tBtn} title="Numbered list" data-testid="inline-fmt-numbered"><ListOrdered size={14} /></button>
      </div>
      <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-ol-line">
        <span className="mr-auto text-[11px] text-ol-sub">Sending from {activeEmail} · your signature is added automatically</span>
        <button onClick={() => setOpen(false)} className="text-[13px] text-ol-sub hover:text-ol-text px-3 py-1.5" data-testid="inline-reply-discard">Discard</button>
        <button onClick={send} disabled={busy} className="flex items-center gap-2 bg-ol-blue hover:bg-ol-bluehover text-white font-semibold text-[13px] px-5 py-1.5 rounded-[4px] transition-colors disabled:opacity-60" data-testid="inline-reply-send">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {busy ? "Sending…" : "Send"}
        </button>
      </div>
    </div>
  );
};
